import moment from "moment-timezone";

export const _date = (date, {
    time = false,
    relative = false,
    relativeOnly = false,
    long = true,
    year = true
} = {}) => {
    const m = moment(date);

    if (!m.isValid()) return "Invalid date";

    let formatted;
    let format = long ? "DD MMM " : "DD.MM.";
    if (year) format = `${format}YYYY`
    if (time) format = ` HH:mm, ${format}`

    formatted = m.format(format);

    if (relativeOnly) return m.fromNow();
    if (relative) return `${formatted} (${m.fromNow()})`;

    return formatted;
}

export function formatWithTimezone(datetime, targetTimezone, useLocal = false, format = "YYYY-MM-DD HH:mm") {
    if (!datetime) return "N/A";

    const tz = useLocal ? moment.tz.guess() : targetTimezone || moment.tz.guess();
    return moment.utc(datetime).tz(tz).format(format);
}

export const duration = (startedAt, completedAt) => {
    let duration = "N/A";

    if (startedAt && completedAt) {
        const start = moment(startedAt);
        const end = moment(completedAt);
        const diff = moment.duration(end.diff(start));

        const hours = diff.hours() > 0 ? `${diff.hours()}h` : '';
        const minutes = diff.minutes() > 0 ? `${diff.minutes()}m` : '';
        const seconds = diff.seconds() > 0 ? `${diff.seconds()}s` : 'Less then 1s';


        duration = `${hours} ${minutes} ${seconds}`;
    }

    return duration;
}

export const capitalize = s => s && String(s[0]).toUpperCase() + String(s).slice(1);

export const _number = s => s && parseInt(s).toLocaleString('en-US');

export const flattenTree = (tree, children = 'children') => {
    const result = [];

    function recurse(node) {
        result.push({ ...node, [children]: undefined });
        if (Array.isArray(node[children])) {
            node[children].forEach(recurse);
        }
    }

    if (Array.isArray(tree)) {
        tree.forEach(recurse);
    }

    return result;
}

export const getStatusChipColor = (status, row) => {
    const lowerCaseStatus = status ? String(status).toLowerCase() : '';

    switch (lowerCaseStatus) {
        case 'sending':
            return row?.type === 'api' ? "success" : 'info';
        case 'scheduled':
            return 'primary';
        case 'sent':
            return 'success';
        case 'filtering_failed':
        case 'failed':
            return 'error';
        case 'blocked':
        case 'stopped':
            return 'warning';
        default:
            return 'default';
    }
};

export const getStatusValue = (status, row) => {

    const lowerCaseStatus = status ? String(status).toLowerCase() : '';

    switch (lowerCaseStatus) {
        case 'sending':
            return row.type === 'api' ? "ACTIVE" : "SENDING";
        case 'scheduled':
            return 'SCHEDULED';
        case 'sent':
            return 'SENT';
        case 'failed':
            return 'FAILED';
        case 'blocked':
            return 'BLOCKED';
        case 'stopped':
            return 'STOPPED';
        case 'filtering_failed':
            return 'FAILED';
        default:
            return 'UNKNOWN';
    }
};

export const _mask = (value, start = 4, end = 3) => {
    if (!value) return "";
    if (value.length <= start + end) return value;
    return `${value.slice(0, start)}***${value.slice(-end)}`;
}

export const getReputationStatus = (domain) => {
    const map = {
        BAD: "error",
        LOW: "warning",
        MEDIUM: "primary",
        HIGH: "success",
    }

    return map[domain.reputation || "unknown"];
};

export const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

export const DRAM_SYMBOL = "֏";

export const formatCurrency = (amount) => {
    if (!amount || amount === 0) return `0.00 ${DRAM_SYMBOL}`;

    const absAmount = Math.abs(amount);
    const isNegative = amount < 0;
    const suffix = ` ${DRAM_SYMBOL}`;
    const prefix = isNegative ? "-" : "";

    if (absAmount < 0.01) {
        if (absAmount < 0.0001) {
            return `< 0.01${suffix}`;
        }
        return `${prefix}${absAmount.toFixed(4)}${suffix}`;
    }

    if (absAmount < 1000) {
        return `${prefix}${absAmount.toFixed(2)}${suffix}`;
    }

    if (absAmount < 1000000) {
        return `${prefix}${absAmount.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        })}${suffix}`;
    }

    if (absAmount >= 1000000000) {
        return `${prefix}${(absAmount / 1000000000).toFixed(1)}B${suffix}`;
    }

    if (absAmount >= 1000000) {
        return `${prefix}${(absAmount / 1000000).toFixed(1)}M${suffix}`;
    }

    return `${prefix}${absAmount.toFixed(2)}${suffix}`;
};

export const formatMoney = (amount) => {
    const numericAmount = Number(amount);
    const safeAmount = Number.isFinite(numericAmount) ? numericAmount : 0;
    const formatted = safeAmount.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    return `${formatted} ${DRAM_SYMBOL}`;
};